import React, { useMemo } from 'react';
import { Link } from '@remix-run/react';
import { FileIcon, InfoIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { Separator } from '@rocco/components/separator';
import { Spinner } from '@rocco/components/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { SystemMessageType } from 'generated/graphql';
import { RICH_TEXT_COMPACT_CLASS } from '~/utils/rich-text';
import { localizeTime } from '~/modules/root';
import { useUserLoggedIn } from '~/modules/user';
import { isImageFile, isPdfFile } from '~/modules/file';
import type {
    Conversation,
    EnquiryProfessional,
    Message,
    MessageAttachment,
    ReplyToMessage,
    TextOrFileMessage,
} from '../../types';
import {
    formatDateFull,
    formatDatePartial,
    getDirectChatParticipants,
    getParticipantNames,
    linkifyText,
} from '../../utils';
import { useChat } from '../../hooks/use-chat';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';
import { ChatMessageActions } from './ChatMessageActions';
import { ChatMessageAttachments } from './ChatMessageAttachments';
import { ChatMessageImageGrid } from './ChatMessageImageGrid';
import { ChatPDFPreview } from './ChatPDFPreview';

const TOOLTIP_DELAY_DURATION = 800;

/**
 * A message bubble representing a message in a conversation.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export function ChatMessageBubble({
    message,
    conversation,
    professional,
    hideSender = false,
    hideActions = false,
    onReply,
    onForward,
    onDelete,
}: {
    /**
     * The conversation message.
     */
    message: Message;
    /**
     * The conversation the message belongs to.
     */
    conversation?: Conversation;
    /**
     * The professional of from the initial enquiry, if this is for an EnquiryConversation.
     */
    professional?: EnquiryProfessional;
    /**
     * Hide the sender name and avatar. This can be set if the previous message
     * was from the same sender. Defaults to `false`.
     */
    hideSender?: boolean;
    /**
     * Hide the actions for this message bubble. Defaults to `false`.
     */
    hideActions?: boolean;
    /**
     * Callback when the reply button is clicked.
     */
    onReply?: (message: TextOrFileMessage) => void;
    /**
     * Callback when the forward button is clicked.
     */
    onForward?: (message: TextOrFileMessage) => void;
    /**
     * Callback when the delete button is clicked.
     */
    onDelete?: () => void;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const senderParticipant = message.Participant;
    const sender = senderParticipant.Identity;
    const isOutgoing =
        isLoggedIn && sender.__typename === 'Member' && sender.ID === user.ID;
    const senderName = useMemo(() => {
        if (sender.__typename === 'Professional') {
            return message.CreatedBy
                ? `${message.CreatedBy} (${sender.Title})`
                : sender.Title;
        } else if (message.CreatedBy) {
            return message.CreatedBy;
        }
        return sender.Name;
    }, [message.CreatedBy, sender]);

    return (
        <>
            {!hideSender &&
                !isOutgoing &&
                message.__typename !== 'SystemMessage' &&
                message.__typename !== 'RevealContactDetailsMessage' && (
                    <div className="flex w-full items-center gap-[8px]">
                        <ParticipantAvatar
                            className="size-[28px]"
                            fallbackClassName="!text-[13px]"
                            participant={
                                // Show the professional avatar if the sender is a collaborator.
                                senderParticipant.__typename ===
                                    'EnquiryParticipant' &&
                                senderParticipant.IsCollaborator &&
                                professional
                                    ? professional
                                    : sender
                            }
                        />
                        <span className="min-w-0 flex-1 break-words text-[12px] font-medium leading-tight">
                            {senderName}
                        </span>
                    </div>
                )}
            <div
                className={cn(
                    'flex w-full flex-col gap-[8px]',
                    isOutgoing ? 'items-end' : 'items-start'
                )}
            >
                {message.__typename === 'TextMessage' && (
                    <TextMessageBubble
                        conversationID={
                            conversation ? Number(conversation.ID) : 0
                        }
                        message={message}
                        content={message.Content}
                        replyTo={message.ReplyTo}
                        isHTML={message.IsHTML}
                        isOptimistic={message.isOptimistic ?? false}
                        isOutgoing={isOutgoing}
                        hideActions={hideActions}
                        onReply={onReply}
                        onForward={onForward}
                        onDelete={onDelete}
                    />
                )}
                {message.__typename === 'FileAttachmentMessage' && (
                    <FileAttachmentMessageBubble
                        conversationID={
                            conversation ? Number(conversation.ID) : 0
                        }
                        message={message}
                        content={message.Content}
                        replyTo={message.ReplyTo}
                        attachments={message.Attachments}
                        isHTML={message.IsHTML}
                        isOptimistic={message.isOptimistic ?? false}
                        isOutgoing={isOutgoing}
                        hideActions={hideActions}
                        onReply={onReply}
                        onForward={onForward}
                        onDelete={onDelete}
                    />
                )}
                {message.__typename === 'DeletedMessage' && (
                    <DeletedMessageBubble
                        message={message}
                        isOutgoing={isOutgoing}
                    />
                )}
                {message.__typename === 'SystemMessage' && (
                    <SystemMessageBubble message={message} />
                )}
                {message.__typename === 'MemberEnquiryMessage' && (
                    <MemberEnquiryMessageBubble
                        conversationID={
                            conversation ? Number(conversation.ID) : 0
                        }
                        message={message}
                        isOutgoing={isOutgoing}
                    />
                )}
                {message.__typename === 'RevealContactDetailsMessage' && (
                    <RevealContactDetailsMessageBubble message={message} />
                )}
            </div>
        </>
    );
}

function TextMessageBubble({
    conversationID,
    message,
    content,
    replyTo,
    isHTML,
    isOptimistic,
    isOutgoing,
    hideActions = false,
    onReply,
    onForward,
    onDelete,
}: {
    conversationID: number;
    message: Message;
    content: string;
    replyTo?: ReplyToMessage | null;
    isHTML: boolean;
    isOptimistic: boolean;
    isOutgoing: boolean;
    hideActions?: boolean;
    onReply?: (message: TextOrFileMessage) => void;
    onForward?: (message: TextOrFileMessage) => void;
    onDelete?: () => void;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const linkifiedContent = useMemo(() => {
        return isHTML
            ? [content]
            : linkifyText(content, 'font-medium !underline');
    }, [content, isHTML]);

    return (
        <article
            className={cn(
                'group/message flex w-full items-center gap-[4px]',
                isOutgoing ? 'flex-row-reverse' : 'flex-row'
            )}
        >
            <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div
                            className={cn(
                                'w-fit min-w-[40%] max-w-[80%] space-y-[2px] break-words rounded-[8px] p-[8px] text-[12px] leading-normal tracking-wide',
                                isOutgoing
                                    ? 'bg-black/40 text-white'
                                    : 'bg-white text-black'
                            )}
                        >
                            {replyTo && (
                                <QuotedReply
                                    conversationID={conversationID}
                                    replyTo={replyTo}
                                    isOutgoing={isOutgoing}
                                />
                            )}
                            {isHTML ? (
                                <div
                                    className={cn(
                                        RICH_TEXT_COMPACT_CLASS,
                                        'whitespace-pre-wrap'
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html: content,
                                    }}
                                ></div>
                            ) : (
                                <div className="whitespace-pre-wrap">
                                    {linkifiedContent}
                                </div>
                            )}
                            <div className="flex items-center justify-end gap-[4px] text-[10px] opacity-70">
                                <time
                                    className="flex-1 truncate text-right"
                                    dateTime={a11yDateString}
                                >
                                    {createdAtString}
                                </time>
                                {isOptimistic && (
                                    <Spinner className="size-[12px] shrink-0" />
                                )}
                            </div>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side={isOutgoing ? 'left' : 'right'}>
                        {tooltipDateString}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            {!hideActions && !isOptimistic && (
                <ChatMessageActions
                    className="opacity-0 transition-opacity duration-100 group-hover/message:opacity-100"
                    message={message}
                    isOutgoing={isOutgoing}
                    onReply={() => {
                        if (
                            message.__typename === 'TextMessage' ||
                            message.__typename === 'FileAttachmentMessage'
                        ) {
                            onReply?.(message);
                        }
                    }}
                    onForward={() => {
                        if (
                            message.__typename === 'TextMessage' ||
                            message.__typename === 'FileAttachmentMessage'
                        ) {
                            onForward?.(message);
                        }
                    }}
                    onDelete={onDelete}
                />
            )}
        </article>
    );
}

/**
 * A quoted reply representing a message being replied to or forwarded.
 */
function QuotedReply({
    conversationID,
    replyTo,
    isOutgoing,
}: {
    conversationID: number;
    replyTo: ReplyToMessage;
    isOutgoing: boolean;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(replyTo.Created);
    }, [replyTo.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const senderName = useMemo(() => {
        const sender = replyTo.Participant.Identity;
        if (sender.__typename === 'Professional') {
            return sender.Title;
        }
        return sender.Name;
    }, [replyTo.Participant]);
    const linkifiedContent = useMemo(() => {
        return replyTo.IsHTML
            ? [replyTo.Content]
            : linkifyText(replyTo.Content, 'font-medium !underline');
    }, [replyTo]);
    const attachments =
        replyTo.__typename === 'FileAttachmentMessage'
            ? replyTo.Attachments
            : null;

    return (
        <blockquote
            className={cn(
                'mb-[6px] border-l-[2px] pl-[6px] text-[11px] leading-normal',
                isOutgoing
                    ? 'border-l-white/50 text-white/80'
                    : 'border-l-black/30 text-black/70'
            )}
        >
            <cite className="font-medium not-italic">{senderName}</cite>
            {replyTo.Content &&
                (replyTo.IsHTML ? (
                    <div
                        className={cn(
                            RICH_TEXT_COMPACT_CLASS,
                            'whitespace-pre-wrap'
                        )}
                        dangerouslySetInnerHTML={{ __html: replyTo.Content }}
                    />
                ) : (
                    <div className="whitespace-pre-wrap">
                        {linkifiedContent}
                    </div>
                ))}
            {attachments && attachments.length > 0 && (
                <ul className="mt-[4px] flex flex-col gap-[4px]">
                    {attachments.map((attachment) => (
                        <li key={attachment.ID} className="w-full">
                            <a
                                className={cn(
                                    'flex items-center gap-[6px] rounded-[4px] p-[4px]',
                                    isOutgoing
                                        ? 'bg-white/10 hover:bg-white/20'
                                        : 'bg-black/5 hover:bg-black/10'
                                )}
                                href={attachment.Link}
                                target="_blank"
                                rel="noopener"
                                download
                            >
                                <FileIcon className="size-[14px] shrink-0" />
                                <span className="truncate">
                                    {attachment.FileName}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            )}
            {/* Show timestamp only if forwarded message (not the same conversation). */}
            {conversationID > 0 &&
                replyTo.ConversationID !== conversationID && (
                    <div className="flex items-center justify-end text-[10px]">
                        <time
                            className="truncate text-right"
                            dateTime={a11yDateString}
                        >
                            Forwarded &middot; {createdAtString}
                        </time>
                    </div>
                )}
        </blockquote>
    );
}

function FileAttachmentMessageBubble({
    conversationID,
    message,
    content,
    replyTo,
    attachments,
    isHTML,
    isOptimistic,
    isOutgoing,
    hideActions = false,
    onReply,
    onForward,
    onDelete,
}: {
    conversationID: number;
    message: Message;
    content?: string | null;
    replyTo?: ReplyToMessage | null;
    attachments: MessageAttachment[];
    isHTML: boolean;
    isOptimistic: boolean;
    isOutgoing: boolean;
    hideActions?: boolean;
    onReply?: (message: TextOrFileMessage) => void;
    onForward?: (message: TextOrFileMessage) => void;
    onDelete?: () => void;
}) {
    const singlePDF =
        attachments.length === 1 && isPdfFile(attachments[0]?.FileName)
            ? attachments[0]
            : undefined;
    const isAllImages =
        attachments.length > 0 &&
        attachments.every((att) => isImageFile(att.FileName));
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);

    return (
        <>
            {(!!content || !!replyTo) && (
                <TextMessageBubble
                    conversationID={conversationID}
                    message={message}
                    content={content ?? ''}
                    replyTo={replyTo}
                    isHTML={isHTML}
                    isOptimistic={isOptimistic}
                    isOutgoing={isOutgoing}
                    hideActions={hideActions}
                    onReply={onReply}
                    onForward={onForward}
                    onDelete={onDelete}
                />
            )}
            {attachments.length > 0 && (
                <article
                    className={cn(
                        'group/message flex w-full items-center gap-[4px]',
                        isOutgoing ? 'flex-row-reverse' : 'flex-row'
                    )}
                >
                    {singlePDF ? (
                        <ChatPDFPreview
                            className="w-[70%]"
                            attachment={singlePDF}
                            date={createdAt}
                            isOutgoing={isOutgoing}
                            isOptimistic={isOptimistic}
                        />
                    ) : isAllImages ? (
                        <ChatMessageImageGrid
                            className="w-[80%]"
                            images={attachments}
                            date={createdAt}
                            isOutgoing={isOutgoing}
                            isOptimistic={isOptimistic}
                        />
                    ) : (
                        <ChatMessageAttachments
                            className="w-[60%]"
                            attachments={attachments}
                            date={createdAt}
                            isOutgoing={isOutgoing}
                            isOptimistic={isOptimistic}
                            tooltipDelayMs={TOOLTIP_DELAY_DURATION}
                        />
                    )}
                    {!hideActions && !isOptimistic && (
                        <ChatMessageActions
                            className="opacity-0 transition-opacity duration-100 group-hover/message:opacity-100"
                            message={message}
                            isOutgoing={isOutgoing}
                            onReply={() => {
                                if (
                                    message.__typename === 'TextMessage' ||
                                    message.__typename ===
                                        'FileAttachmentMessage'
                                ) {
                                    onReply?.(message);
                                }
                            }}
                            onForward={() => {
                                if (
                                    message.__typename === 'TextMessage' ||
                                    message.__typename ===
                                        'FileAttachmentMessage'
                                ) {
                                    onForward?.(message);
                                }
                            }}
                            onDelete={onDelete}
                        />
                    )}
                </article>
            )}
        </>
    );
}

function DeletedMessageBubble({
    message,
    isOutgoing,
}: {
    message: Extract<Message, { __typename: 'DeletedMessage' }>;
    isOutgoing: boolean;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <article
                        className={cn(
                            'w-fit min-w-[40%] max-w-[80%] break-words rounded-[8px] p-[8px] text-[12px] italic leading-normal tracking-wide',
                            isOutgoing
                                ? 'bg-black/40 text-white'
                                : 'bg-white text-black'
                        )}
                    >
                        This message has been deleted
                    </article>
                </TooltipTrigger>
                <TooltipContent side={isOutgoing ? 'left' : 'right'}>
                    {tooltipDateString}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function SystemMessageBubble({
    message,
}: {
    message: Extract<Message, { __typename: 'SystemMessage' }>;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { openChatBox } = useChat();
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const senderName = useMemo(() => {
        const sender = message.Participant.Identity;
        if (sender.__typename === 'Professional') {
            return message.CreatedBy
                ? `${message.CreatedBy} (${sender.Title})`
                : sender.Title;
        } else if (message.CreatedBy) {
            return message.CreatedBy;
        }
        return sender.Name;
    }, [message]);
    const messageText = useMemo(() => {
        if (message.Type === SystemMessageType.NameChanged) {
            if (message.Value) {
                return (
                    <>
                        <strong>{senderName}</strong> named this conversation{' '}
                        <strong>"{message.Value}"</strong>
                    </>
                );
            }
            return (
                <>
                    <strong>{senderName}</strong> removed the conversation title
                </>
            );
        } else if (message.Type === SystemMessageType.BuildHubChanged) {
            if (message.ValueBuildHub) {
                return (
                    <>
                        <strong>{senderName}</strong> updated the project to{' '}
                        <strong>"{message.ValueBuildHub.Title}"</strong>
                    </>
                );
            }
            return (
                <>
                    <strong>{senderName}</strong> removed the linked project
                </>
            );
        } else if (message.Type === SystemMessageType.ThumbnailChanged) {
            if (message.ValueID > 0) {
                return (
                    <>
                        <strong>{senderName}</strong> updated the chat thumbnail
                    </>
                );
            }
            return (
                <>
                    <strong>{senderName}</strong> removed the chat thumbnail
                </>
            );
        } else if (message.Type === SystemMessageType.ParticipantAdded) {
            const addedParticipant = message.ValueParticipant?.Identity;
            return (
                <>
                    <strong>{senderName}</strong> added{' '}
                    <strong>
                        {addedParticipant?.__typename === 'Professional'
                            ? addedParticipant.Title
                            : addedParticipant?.Name}
                    </strong>{' '}
                    to this conversation
                </>
            );
        } else if (message.Type === SystemMessageType.ParticipantRemoved) {
            if (message.Participant.ID === message.ValueParticipant?.ID) {
                return (
                    <>
                        <strong>{senderName}</strong> left this conversation
                    </>
                );
            }
            const removedParticipant = message.ValueParticipant?.Identity;
            return (
                <>
                    <strong>{senderName}</strong> removed{' '}
                    <strong>
                        {removedParticipant?.__typename === 'Professional'
                            ? removedParticipant.Title
                            : removedParticipant?.Name}
                    </strong>{' '}
                    from this conversation
                </>
            );
        } else if (message.Type === SystemMessageType.MessagesMovedOut) {
            const targetChat = message.ValueConversation;
            const otherParticipants = getDirectChatParticipants(
                targetChat,
                isLoggedIn ? user.ID : 0
            );
            return (
                <>
                    <strong>{senderName}</strong> moved some of the messages
                    from this conversation to{' '}
                    {targetChat ? (
                        <>
                            {!targetChat.Name && 'one with '}
                            <strong
                                className="cursor-pointer underline"
                                onClick={() => {
                                    if (targetChat) {
                                        openChatBox(Number(targetChat.ID));
                                    }
                                }}
                            >
                                {targetChat.Name ??
                                    getParticipantNames(otherParticipants, 3)}
                            </strong>
                        </>
                    ) : (
                        'another one'
                    )}
                </>
            );
        } else if (message.Type === SystemMessageType.MessagesMovedIn) {
            return (
                <>
                    <strong>{senderName}</strong> moved messages from a previous
                    conversation to this one
                </>
            );
        }
        console.warn('Unknown system message type', message.Type);
        return <></>;
    }, [message, senderName, user.ID, isLoggedIn, openChatBox]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION * 3}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="w-full break-words px-[8px] py-[4px] text-center text-[12px] leading-normal tracking-wide text-gray-800">
                        {messageText}
                    </div>
                </TooltipTrigger>
                <TooltipContent side="top">{tooltipDateString}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function MemberEnquiryMessageBubble({
    conversationID,
    message,
    isOutgoing,
}: {
    conversationID: number;
    message: Extract<Message, { __typename: 'MemberEnquiryMessage' }>;
    isOutgoing: boolean;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const items = useMemo(() => {
        return message.LibraryItems.filter(
            (item) => item.__typename !== 'Professional'
        );
    }, [message.LibraryItems]);

    // TODO: Tracker: CommentLibraryItems.tsx

    return items.length ? (
        <>
            <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <article
                            className={cn(
                                'w-fit min-w-[60%] max-w-[80%] overflow-hidden break-words rounded-[8px] text-[12px] leading-normal tracking-wide',
                                isOutgoing
                                    ? 'bg-black/40 text-white'
                                    : 'bg-white text-black'
                            )}
                        >
                            {items.map(
                                ({
                                    ID,
                                    Title,
                                    Link: link,
                                    Professional,
                                    LibraryThumb,
                                }) => (
                                    <React.Fragment key={ID}>
                                        <Link
                                            to={link}
                                            className={cn(
                                                'block w-full p-[8px] leading-tight',
                                                isOutgoing
                                                    ? 'hover:bg-black/30'
                                                    : 'hover:bg-black/20'
                                            )}
                                        >
                                            {!!LibraryThumb?.ThumbImageFile
                                                ?.Thumbnail.Desktop && (
                                                <Image
                                                    className="mb-[12px] w-[140px]"
                                                    src={
                                                        LibraryThumb
                                                            .ThumbImageFile
                                                            .Thumbnail.Desktop
                                                    }
                                                    alt={Title}
                                                    fit="contain"
                                                    fadeIn={false}
                                                />
                                            )}
                                            <div className="mb-[2px] font-medium">
                                                {Title}
                                            </div>
                                            <div className="opacity-70">
                                                {Professional?.Title}
                                            </div>
                                        </Link>
                                        <div className="px-[8px]">
                                            <Separator
                                                className={cn(
                                                    isOutgoing
                                                        ? 'bg-white/20'
                                                        : 'bg-border'
                                                )}
                                            />
                                        </div>
                                    </React.Fragment>
                                )
                            )}
                            <div className="space-y-[2px] p-[8px]">
                                <div
                                    className={cn(
                                        RICH_TEXT_COMPACT_CLASS,
                                        'whitespace-pre-wrap'
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html: message.Content,
                                    }}
                                ></div>
                                <time
                                    className="block flex-1 truncate text-right text-[10px] opacity-70"
                                    dateTime={a11yDateString}
                                >
                                    {createdAtString}
                                </time>
                            </div>
                        </article>
                    </TooltipTrigger>
                    <TooltipContent side={isOutgoing ? 'left' : 'right'}>
                        {tooltipDateString}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            {message.Attachments.length > 0 && (
                <FileAttachmentMessageBubble
                    conversationID={conversationID}
                    message={message}
                    content={undefined}
                    attachments={message.Attachments}
                    isHTML
                    isOptimistic={message.isOptimistic ?? false}
                    isOutgoing={isOutgoing}
                />
            )}
        </>
    ) : (
        <FileAttachmentMessageBubble
            conversationID={conversationID}
            message={message}
            content={message.Content}
            attachments={message.Attachments}
            isHTML
            isOptimistic={message.isOptimistic ?? false}
            isOutgoing={isOutgoing}
        />
    );
}

function RevealContactDetailsMessageBubble({
    message,
}: {
    message: Extract<Message, { __typename: 'RevealContactDetailsMessage' }>;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="w-full space-y-[2px] rounded-[8px] bg-white p-[8px] leading-normal tracking-wide text-black">
                        <div className="flex items-center gap-[8px] break-words">
                            <InfoIcon className="size-[16px]" />
                            <div className="min-w-0 flex-1 text-[12px]">
                                {message.Content}
                            </div>
                        </div>
                        <time
                            className="block truncate text-right text-[10px] opacity-70"
                            dateTime={a11yDateString}
                        >
                            {createdAtString}
                        </time>
                    </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                    {tooltipDateString}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
